import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  SendHorizonal,
  Sparkles,
  TriangleAlert,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  appendRecordVersion,
  createNextVersionId,
  getRecordVersion,
  getRecordWithVersions,
  getKindVisual,
  type ChatMsg,
  type EvaluationVersion,
  type ScoreBar,
  updateRecordVersion,
} from "../mocks/evaluation-records";
import { ProgressBoard, type ProgressNode } from "../components/ProgressBoard";

export default function InnovationRecordDetail() {
  const { recordId } = useParams<{ recordId: string }>();
  const [tick, setTick] = useState(0);
  const record = useMemo(() => (recordId ? getRecordWithVersions(recordId) : undefined), [recordId, tick]);
  const [tab, setTab] = useState<"report" | "chat">("report");
  const [versionMenuOpen, setVersionMenuOpen] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunNodes, setRerunNodes] = useState<ProgressNode[]>([]);
  const [rerunRunning, setRerunRunning] = useState(false);

  const [composer, setComposer] = useState("");
  const [localMsgs, setLocalMsgs] = useState<ChatMsg[]>([]);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 切换记录时重置本地对话输入与临时消息
    setComposer("");
    setLocalMsgs([]);
    setTab("report");
    setActiveVersionId(null);
    setVersionMenuOpen(false);
    setRerunOpen(false);
    setRerunRunning(false);
    setRerunNodes([]);
  }, [recordId]);

  useEffect(() => {
    // 滚动到底部
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [tab, recordId, localMsgs.length]);

  if (!record) {
    return (
      <div className="rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        <p className="text-sm text-muted-foreground">未找到该评价记录或链接已失效。</p>
        <Button className="mt-6" variant="outline" asChild>
          <Link to="/innovation/records">返回评价记录</Link>
        </Button>
      </div>
    );
  }

  const activeVersion: EvaluationVersion = getRecordVersion(record, activeVersionId);
  const v = getKindVisual(activeVersion.kind);
  const Icon = v.Icon;
  // 更克制：状态仅用文字语义色，不铺大面积彩色底
  const scoreTone = activeVersion.status === "优化中" ? "text-muted-foreground" : "text-primary";

  const baseMsgs = activeVersion.chat?.messages ?? [];
  // 顺序：上面老的、下面新的（保持原数组顺序；本地消息只追加到末尾）
  const chatMsgs = useMemo(() => [...baseMsgs, ...localMsgs], [baseMsgs, localMsgs]);
  const suggestions = useMemo(() => {
    const fromData = activeVersion.chat?.suggestions ?? [];
    if (fromData.length) return fromData;
    // fallback: derive from current version report
    const ks = (activeVersion.report?.keyIssues ?? []).slice(0, 2);
    const rs = (activeVersion.report?.risks ?? []).slice(0, 1);
    const items = [
      "把关键发现转成可执行修改清单",
      "生成一段管理层摘要（100字以内）",
      ks[0] ? `围绕「${ks[0]}」给出改写建议` : "",
      rs[0] ? `针对风险「${rs[0]}」给出分级与对策` : "",
      "给出下一轮迭代的行动路径（立即/近期/中期）",
    ].filter(Boolean) as string[];
    return items;
  }, [activeVersion.chat?.suggestions, activeVersion.report]);

  const scoreBars = activeVersion.report?.scoreBars ?? [];
  const inputTitle = activeVersion.report?.inputTitle ?? "输入内容";
  const inputContent = activeVersion.report?.inputContent ?? "";
  const conclusion = activeVersion.report?.conclusion ?? record.summary;
  const keyIssues = activeVersion.report?.keyIssues ?? record.improvements;
  const advantages = activeVersion.report?.advantages ?? record.highlights;
  const risks = activeVersion.report?.risks ?? [];

  const startRerun = () => {
    if (!recordId) return;
    if (rerunRunning) return;
    setRerunOpen(true);
    setRerunRunning(true);
    const nodes: ProgressNode[] = [
      { id: "parse", title: "输入解析", desc: "提取摘要 / 元信息 / 结构化字段", status: "queued", logs: [] },
      { id: "retrieve", title: "文献与对标检索", desc: "检索同类项目 / 论文 / 专利", status: "queued", logs: [] },
      { id: "score", title: "维度打分", desc: "按所选维度生成分项评分", status: "queued", logs: [] },
      { id: "findings", title: "关键发现生成", desc: "优势 / 风险 / 关键问题", status: "queued", logs: [] },
      { id: "compose", title: "报告排版", desc: "输出报告结构与行动路径", status: "queued", logs: [] },
      { id: "done", title: "完成", desc: "生成新版本", status: "queued", logs: [] },
    ];
    setRerunNodes(nodes);

    const t0 = Date.now();
    const setNode = (id: string, status: ProgressNode["status"], log?: string) => {
      setRerunNodes((prev) =>
        prev.map((n) => {
          if (n.id !== id) return n;
          const logs = [...(n.logs || [])];
          if (log) logs.push(log);
          const costMs = status === "done" ? Date.now() - t0 : n.costMs;
          return { ...n, status, logs, costMs: status === "done" ? costMs : n.costMs };
        }),
      );
    };

    window.setTimeout(() => setNode("parse", "running", "开始解析输入材料…"), 120);
    window.setTimeout(() => setNode("parse", "done", "解析完成（演示）。"), 900);
    window.setTimeout(() => setNode("retrieve", "running", "检索对标样本（演示）…"), 980);
    window.setTimeout(() => setNode("retrieve", "done", "命中样本 9 条（演示）。"), 1800);
    window.setTimeout(() => setNode("score", "running", `按维度「${activeVersion.kind}」开始打分…`), 1860);
    window.setTimeout(() => setNode("score", "done", "分项评分已生成（演示）。"), 2600);
    window.setTimeout(() => setNode("findings", "running", "生成关键发现…"), 2660);
    window.setTimeout(() => setNode("findings", "done", "关键发现已生成（演示）。"), 3400);
    window.setTimeout(() => setNode("compose", "running", "排版报告结构…"), 3460);
    window.setTimeout(() => setNode("compose", "done", "报告排版完成（演示）。"), 4100);
    window.setTimeout(() => setNode("done", "done", "生成新版本完成。"), 4300);

    window.setTimeout(() => {
      const nextId = createNextVersionId(record);
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const nextScore = Math.min(95, Math.max(60, activeVersion.score + 2));
      const next: EvaluationVersion = {
        id: nextId,
        createdAt: `${now.toISOString().slice(0, 10)} ${hh}:${mm}`,
        status: "已完成",
        score: nextScore,
        kind: activeVersion.kind,
        input: activeVersion.input,
        report: {
          ...(activeVersion.report ?? {}),
          conclusion: `（v${nextId.replace(/^v/i, "")}）已根据对话建议进行了迭代优化：收敛创新点、补齐对标口径、量化里程碑（演示）。`,
          keyIssues: [
            "对标样本口径已补齐：范围/检索策略/排除条件（演示）",
            "关键指标已量化并绑定里程碑验收口径（演示）",
            "风险分级补充触发条件与应对策略（演示）",
          ],
        },
        chat: activeVersion.chat,
        progress: { nodes: rerunNodes.map((n) => ({ id: n.id, title: n.title, status: n.status, costMs: n.costMs, logs: n.logs })) },
      };
      appendRecordVersion(recordId, next);
      setRerunRunning(false);
      setRerunOpen(false);
      setActiveVersionId(nextId);
      setTick((x) => x + 1);
    }, 4450);
  };

  const send = (text: string) => {
    const val = text.trim();
    if (!val) return;
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const makeReply = () => {
      const conclusion = activeVersion.report?.conclusion ?? record.summary;
      const keyIssues = (activeVersion.report?.keyIssues ?? record.improvements).slice(0, 3);
      const advantages = (activeVersion.report?.advantages ?? record.highlights).slice(0, 3);
      const risks = (activeVersion.report?.risks ?? []).slice(0, 2);
      const mention = activeVersion.kind;
      const head = `（基于 ${activeVersion.id} · ${mention}）我建议你按“问题→动作→验收口径”来迭代。`;
      const lines: string[] = [head, "", "你当前版本的关键点："];
      if (keyIssues.length) {
        lines.push("关键发现（优先修）：");
        keyIssues.forEach((x, i) => lines.push(`${i + 1}. ${x}`));
        lines.push("");
      }
      if (advantages.length) {
        lines.push("可保留亮点：");
        advantages.forEach((x) => lines.push(`- ${x}`));
        lines.push("");
      }
      if (risks.length) {
        lines.push("风险提示：");
        risks.forEach((x) => lines.push(`- ${x}`));
        lines.push("");
      }
      lines.push("建议下一步（可直接照抄到修订稿）：");
      lines.push("1) 收敛创新点为 2-3 条主线，每条配“证据链（对标/数据/引用）”。");
      lines.push("2) 给每个里程碑补齐“指标+口径+证据交付物”，避免空泛。");
      lines.push("3) 风险分级补充触发条件与应对策略（高/中/低）。");
      lines.push("");
      lines.push(`当前结论：${conclusion}`);
      return lines.join("\n");
    };

    setLocalMsgs((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", time: `${hh}:${mm}`, content: val },
      { id: `local-${Date.now() + 1}`, role: "assistant", time: `${hh}:${mm}`, content: makeReply() },
    ]);
    setComposer("");
  };

  return (
    // 固定在容器内：禁止整页滚动；只在内部框滚动
    // 说明：详情页由 `InnovationLayout` 提供固定高度的 flex 容器，这里只需占满高度即可。
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* 顶部标题区（始终显示，不随滚动消失） */}
      <div className="mb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Link to="/innovation/records" className="hover:text-foreground">
                评价记录
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span className="font-medium text-foreground">详情</span>
            </div>
            <h1 className="mt-2 truncate text-xl font-semibold tracking-tight text-foreground md:text-2xl">{record.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-normal">
                {activeVersion.kind}
              </Badge>
              <span className={cn("text-xs text-muted-foreground")}>{record.date} 14:30</span>
              <span className={cn("rounded-full border border-border bg-background px-2 py-0.5 text-xs font-semibold text-foreground")}>
                {activeVersion.status}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setVersionMenuOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent"
              >
                版本 {activeVersion.id}
                <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", versionMenuOpen && "rotate-180")} />
              </button>
              {versionMenuOpen && (
                <div className="absolute left-0 z-50 mt-2 min-w-40 overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-lg">
                  {record.versions.map((ver) => {
                    const selected = ver.id === activeVersion.id;
                    return (
                      <button
                        key={ver.id}
                        type="button"
                        onClick={() => {
                          setActiveVersionId(ver.id);
                          setVersionMenuOpen(false);
                        }}
                        className={cn(
                          "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition-colors",
                          selected ? "bg-primary/10 text-foreground" : "hover:bg-accent/60",
                        )}
                      >
                        <span className="font-medium">{ver.id}</span>
                        <span className="text-xs text-muted-foreground">{ver.createdAt.slice(5)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Button onClick={startRerun} className="gap-2" disabled={rerunRunning}>
              {rerunRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              重新运行
            </Button>
            <div className="rounded-2xl border border-border bg-background px-4 py-3 text-center shadow-sm">
              <div className={cn("text-2xl font-bold tabular-nums leading-none", scoreTone)}>{activeVersion.score}</div>
              <div className="mt-1 text-[11px] font-medium text-muted-foreground">综合</div>
            </div>
            <div className={cn("hidden h-12 w-12 items-center justify-center rounded-xl md:flex", v.wrap)}>
              <Icon className={cn("h-6 w-6", v.icolor)} />
            </div>
          </div>
        </div>
      </div>

      {rerunOpen ? (
        <div className="mb-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <ProgressBoard nodes={rerunNodes} />
        </div>
      ) : null}

      {/* Tab：评审报告 / 多轮对话 */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setTab("report")}
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
            tab === "report" ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          评审报告
        </button>
        <button
          type="button"
          onClick={() => setTab("chat")}
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
            tab === "chat" ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          <MessageCircle className="h-4 w-4" />
          多轮对话
          <span className={cn("ml-0.5 rounded-full px-2 py-0.5 text-xs", tab === "chat" ? "bg-background/20" : "bg-muted")}>
            {Math.max(0, (activeVersion.chat?.messages?.length ?? 0) + localMsgs.length)}
          </span>
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {tab === "report" ? (
          // 评审报告：用整块卡片占满高度，内部滚动，避免底部出现“页面留白”
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="min-h-0 flex-1 overflow-auto p-5 pr-6">
              <div className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  {/* 输入内容 */}
                  <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Sparkles className="h-4 w-4 text-primary" />
                      {inputTitle}
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                      {inputContent || "（演示）此记录尚未关联输入材料内容。"}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["出口管制分析", "前沿问题分析", "重大需求分析"].map((t) => (
                        <span key={t} className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                          {t}
                        </span>
                      ))}
                    </div>
                  </section>

                  {/* 多维评分 */}
                  <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-foreground">多维评分</div>
                      <div className={cn("rounded-full bg-muted px-3 py-1 text-xs font-semibold", scoreTone)}>{activeVersion.score}</div>
                    </div>
                    <div className="mt-4 space-y-3">
                      {scoreBars.length > 0 ? (
                        scoreBars.map((b) => <ScoreRow key={b.label} bar={b} />)
                      ) : (
                        <div className="text-sm text-muted-foreground">（演示）暂无分项评分。</div>
                      )}
                    </div>
                    <div className="mt-4 flex items-start gap-2 rounded-xl border border-border bg-muted/35 p-3 text-sm text-muted-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                      <div>
                        <span className="font-medium text-foreground">综合结论：</span>
                        {conclusion}
                      </div>
                    </div>
                  </section>
                </div>

                {/* 评审摘要 */}
                <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    评审摘要
                  </div>
                  <div className="mt-3 rounded-xl border border-border bg-muted/35 p-4 text-sm leading-relaxed text-muted-foreground">{record.summary}</div>
                </section>

                {/* 关键发现（截图是整行条目） */}
                <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-foreground">关键发现</div>
                    <div className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {keyIssues.length} 条
                    </div>
                  </div>
                  <div className="mt-3 rounded-2xl border border-border bg-muted/15 p-3">
                    <ol className="space-y-2">
                    {keyIssues.map((x, i) => (
                      <li key={`${i}-${x}`} className="flex gap-3 rounded-xl border border-border bg-background/70 p-4 text-sm text-muted-foreground">
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-background text-xs font-semibold text-foreground">
                          {i + 1}
                        </span>
                        <span className="leading-relaxed">{x}</span>
                      </li>
                    ))}
                    </ol>
                  </div>
                </section>

                <div className="grid gap-4 lg:grid-cols-2">
                  {/* 创新亮点 */}
                  <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <div className="text-sm font-semibold text-foreground">创新亮点</div>
                    <ul className="mt-3 space-y-2">
                      {advantages.map((x) => (
                        <li key={x} className="flex gap-2 rounded-xl border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary/70" />
                          <span className="leading-relaxed">{x}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* 改进建议（红/黄提示条） */}
                  <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <div className="text-sm font-semibold text-foreground">改进建议</div>
                    <div className="mt-3 space-y-2">
                      {risks.length > 0 ? (
                        risks.map((x) => (
                          <div key={x} className="flex gap-2 rounded-xl border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
                            <TriangleAlert className="mt-0.5 h-4 w-4 text-muted-foreground" />
                            <div className="leading-relaxed">{x}</div>
                          </div>
                        ))
                      ) : (
                        record.improvements.map((x, idx) => (
                          <div
                            key={x}
                            className={cn(
                              "flex gap-2 rounded-xl border border-border bg-muted/20 p-3 text-sm text-muted-foreground",
                            )}
                          >
                            <TriangleAlert className="mt-0.5 h-4 w-4 text-muted-foreground" />
                            <div className="leading-relaxed">{x}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
      ) : (
        // 多轮对话（全宽，无侧栏）
        <div className="flex min-h-0 flex-1 flex-col rounded-2xl border border-border bg-card shadow-sm">
          <div
            ref={scrollerRef}
            className="min-h-0 flex-1 space-y-3 overflow-auto p-5"
          >
            {chatMsgs.map((m) => (
              <ChatBubble key={m.id} msg={m} />
            ))}
            {chatMsgs.length === 0 && <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">暂无对话内容</div>}
          </div>

          {suggestions.length > 0 && (
            <div className="border-t border-border px-5 py-3">
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="rounded-full border border-border bg-background px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 外层 Layout 已有 20px 底部 padding，这里不要再叠加更大的 bottom padding */}
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3">
              <Input
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder="请输入内容…"
                className="h-11 rounded-xl"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(composer);
                }}
              />
              <Button size="icon" onClick={() => send(composer)} aria-label="发送">
                <SendHorizonal className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">快捷键：Ctrl/⌘ + Enter 发送</div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function ChatBubble({ msg }: { msg: ChatMsg }) {
  const isUser = msg.role === "user";
  return (
    <div className={cn("flex gap-3", isUser ? "justify-end" : "justify-start")}>
      {!isUser && <div className="mt-1 h-8 w-8 shrink-0 rounded-full bg-muted" />}
      <div className={cn("max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed", isUser ? "bg-primary text-primary-foreground" : "bg-muted/40 text-foreground")}>
        <div className="whitespace-pre-wrap">{msg.content}</div>
        <div className={cn("mt-2 text-[11px]", isUser ? "text-primary-foreground/70" : "text-muted-foreground")}>{msg.time}</div>
      </div>
      {isUser && <div className="mt-1 h-8 w-8 shrink-0 rounded-full bg-primary/15" />}
    </div>
  );
}

function toneToCls(tone: ScoreBar["tone"]): string {
  switch (tone) {
    // 更克制：分数条不再多色，仅用品牌色深浅表达
    case "amber":
      return "bg-primary/70";
    case "emerald":
    case "violet":
    case "cyan":
    case "pink":
    default:
      return "bg-primary";
  }
}

function ScoreRow({ bar }: { bar: ScoreBar }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium text-muted-foreground">{bar.label}</span>
        <span className="tabular-nums font-semibold text-foreground">{bar.score}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", toneToCls(bar.tone))} style={{ width: `${Math.min(100, bar.score)}%` }} />
      </div>
    </div>
  );
}
